import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonGroup, Button, HeadingText50, BodyTextMedium } from '@modernhealth/vital-components';

import { usePathwaysNavigation } from 'src/modules/pathways/hooks/navigation';
import type { QuestionnaireType } from 'src/modules/pathways/pages/PathwayCreateQuestionnairePage';

interface Props {
    pathwayUuid: string;
    question?: {
        title: string;
        description: string;
    };
    nextQuestionnaireType?: QuestionnaireType | undefined;
    disabled?: boolean;
    onSubmit?: () => Promise<void> | void;
    children: JSX.Element | JSX.Element[];
}

export const QUESTION_LABEL_ID = 'pathway-questionnaire-question-label';

export const CreatePathwayQuestionnaireLayout = ({
    pathwayUuid: uuid,
    question,
    nextQuestionnaireType,
    disabled,
    onSubmit,
    children,
}: Props) => {
    const { navigate, goBack } = usePathwaysNavigation();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);

    return (
        <>
            {!!question && (
                <>
                    <HeadingText50 marginBottom={12} centered>
                        <label id={QUESTION_LABEL_ID}>{question.title}</label>
                    </HeadingText50>
                    <BodyTextMedium marginBottom={32} centered>
                        {question.description}
                    </BodyTextMedium>
                </>
            )}
            {children}
            <ButtonGroup css={{ marginTop: 40 }}>
                <Button busy={loading} size="large" onClick={goBack} variant="secondary">
                    {t('pathways:action.back')}
                </Button>
                <Button
                    busy={loading}
                    disabled={disabled}
                    size="large"
                    onClick={async () => {
                        setLoading(true);

                        try {
                            await onSubmit?.();

                            if (nextQuestionnaireType) {
                                navigate.createPathwayQuestionnaireType({
                                    uuid,
                                    questionnaireType: nextQuestionnaireType,
                                });
                            } else {
                                navigate.createPathwaySummary({ uuid });
                            }
                        } catch {
                            setLoading(false);
                        }
                    }}
                >
                    {t('pathways:action.continue')}
                </Button>
            </ButtonGroup>
        </>
    );
};
